import * as React from 'react'
import * as Redux from 'react-redux'
import * as jotai from 'jotai'
import styled, { css } from 'styled-components'

import * as consent from '@owl-nest/consent'
import { t } from '@owl-nest/localize'
import * as model from '@owl-nest/models'
import { BREAKPOINTS, COLORS, glyphs, styles as S, WysiwygContent } from '@ulule/owl-kit-components/next'
import * as env from '@owl-nest/config'

import * as modalAction from '../../../../../actions/modal'
import { RootState } from '../../../../../reducers'

import { DisplayMode } from '../../../../context'
import * as projectModel from '../../../../models/project'
import { SCROLL_TO_ATOM } from '../../../../layout/DetailLayout'

import { AccordionWrapper } from '../../components/AccordionWrapper'
import { AdviceModal } from '../../components/AdviceModal'
import { CreateButton } from '../../components/CreateButton'
import { EditButton } from '../../components/EditButton'
import { ProjectFaq } from '../../components/ProjectFaq'
import { useIndice } from '../../hooks/useIndice'
import * as Styles from '../../style'

import { AboutOwner } from './AboutOwner'
import { Explorer } from './Explorer'
import { NewsPush } from './NewsPush'
import { NewToUlule } from './NewToUlule'
import { PushRewardsMobile } from './PushRewardsMobile'
import { SocialFollow } from './SocialFollow'
import * as sidebar from './sidebar'
import * as ProjectTabStyles from './style'

export function ProjectTab(): React.ReactElement<unknown> {
  const dispatch = Redux.useDispatch()
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)
  const latestNews = project.latest_news
  const displayMode = React.useContext(DisplayMode)
  const [isAdviceModalOpen, setAdviceModalOpen] = React.useState(false)
  const isEditMode = displayMode === 'edit' && !model.project.refused<projectModel.ExtraFields>(project)
  const fundingDescription = model.project.fundingDescription<projectModel.ExtraFields>(project)
  const creatorDescription = model.project.ownerDescription<projectModel.ExtraFields>(project)
  const projectIsDonationBased = model.project.isDonationBased(project)
  const userConfig = env.useUserEnv()

  const scrollTo = jotai.useAtomValue(SCROLL_TO_ATOM)

  const aboutOwnerRef = React.useRef<HTMLDivElement>(null)

  const { isOpen, updateOpenIndices } = useIndice()

  function getGlyphAccordion(index: string): React.ReactNode {
    return isOpen(index) ? <glyphs.stroke.CaretUp size={18} /> : <glyphs.stroke.CaretDown size={18} />
  }

  const news = {
    latest: latestNews,
    show: false,
  } as { show: true; latest: NonNullable<typeof latestNews> } | { show: false; latest: typeof latestNews }

  if (latestNews !== undefined) {
    if (!model.project.isFinished<projectModel.ExtraFields>(project)) {
      news.show = true
    } else if (project.date_end) {
      const dateNews = new Date(latestNews.date_publication ?? latestNews.date_creation)
      const dateEndProject = new Date(project.date_end)
      news.show = dateNews > dateEndProject
    }
  }

  React.useEffect(() => {
    // this code is for auto scroll to a block with specific id
    if (scrollTo !== '' && aboutOwnerRef.current) {
      // NOTE: Allow for project tab to load (kinda), to ensure scrolling to the right offset
      setTimeout(() => {
        if (!isOpen(scrollTo)) {
          updateOpenIndices(scrollTo)
        }
        document.location.hash = scrollTo
      }, 300)
    }
  }, [scrollTo])

  return (
    <>
      <Styles.Main isEditMode={isEditMode}>
        <Styles.Wrapper isEditMode={isEditMode}>
          <Styles.Content isEditMode={isEditMode}>
            {news.show && <NewsPush news={news.latest} />}

            <Article isContentSet={true}>
              <ArticleHeading>
                <S.heading.L as="h2">{t('About this project')}</S.heading.L>
              </ArticleHeading>
              {isEditMode && (
                <ProjectTabStyles.EditButtonWrapper>
                  <EditButton link={`/projects/${project.id}/edit/layout/${userConfig.lang}/?description`} />
                </ProjectTabStyles.EditButtonWrapper>
              )}
              <consent.media.Wall rawMarkup={model.project.description<projectModel.ExtraFields>(project) || ''}>
                <WysiwygContent
                  loading="eager"
                  textSize="large"
                  onClickCapture={(e) => {
                    const target = e.target as HTMLElement
                    const anchor = target.closest<HTMLAnchorElement>('a[href]')

                    if (anchor === null) {
                      return
                    }

                    if (anchor.href.includes('?reward=')) {
                      e.preventDefault()

                      const rewardId = new URL(anchor.href).searchParams.get('reward')

                      if (rewardId) {
                        dispatch(modalAction.toggleModalReward(true))
                        dispatch(modalAction.setRewardId(rewardId))
                      }
                    }
                  }}
                />
              </consent.media.Wall>
            </Article>

            {!isEditMode && !projectIsDonationBased && (
              <Styles.OnlyMobileShow>
                <PushRewardsMobile />
              </Styles.OnlyMobileShow>
            )}

            <Article isContentSet={fundingDescription !== ''} open={isOpen('funds')}>
              {fundingDescription === '' ? (
                isEditMode && (
                  <ProjectTabStyles.CreateButtonWrapper>
                    <CreateButton
                      createHandler={() => {
                        document.location.href = `/projects/${project.id}/edit/layout/${userConfig.lang}/?description_funding`
                      }}
                      mode="small"
                      title={<S.heading.XS>{t('What are the funds for?')}</S.heading.XS>}
                    />
                  </ProjectTabStyles.CreateButtonWrapper>
                )
              ) : (
                <>
                  <ArticleHeading
                    onClick={() => {
                      updateOpenIndices('funds')
                    }}
                  >
                    <S.heading.L as="h2">{t('What are the funds for?')}</S.heading.L>
                    {getGlyphAccordion('funds')}
                  </ArticleHeading>

                  <AccordionWrapper open={isOpen('funds')} openOnDesktop={true}>
                    <>
                      {isEditMode && (
                        <ProjectTabStyles.EditButtonWrapper>
                          <EditButton
                            link={`/projects/${project.id}/edit/layout/${userConfig.lang}/?description_funding`}
                          />
                        </ProjectTabStyles.EditButtonWrapper>
                      )}
                      <consent.media.Wall rawMarkup={fundingDescription || ''}>
                        <WysiwygContent
                          loading="eager"
                          textSize="large"
                          onClickCapture={(e) => {
                            const target = e.target as HTMLElement
                            const anchor = target.closest<HTMLAnchorElement>('a[href]')

                            if (anchor === null) {
                              return
                            }

                            if (anchor.href.includes('?reward=')) {
                              e.preventDefault()

                              const rewardId = new URL(anchor.href).searchParams.get('reward')

                              if (rewardId) {
                                dispatch(modalAction.toggleModalReward(true))
                                dispatch(modalAction.setRewardId(rewardId))
                              }
                            }
                          }}
                        />
                      </consent.media.Wall>
                    </>
                  </AccordionWrapper>
                </>
              )}
            </Article>

            <Article isContentSet={creatorDescription !== ''} ref={aboutOwnerRef} open={isOpen('about-owner')}>
              <InvisibleScrollHelper id="about-owner" />
              <ArticleHeading
                onClick={() => {
                  updateOpenIndices('about-owner')
                }}
              >
                <S.heading.L as="h2">{t('About the project owner')}</S.heading.L>
                {getGlyphAccordion('about-owner')}
              </ArticleHeading>

              <AccordionWrapper open={isOpen('about-owner')} openOnDesktop={true}>
                <>
                  {isEditMode && (
                    <ProjectTabStyles.EditButtonWrapper>
                      <EditButton
                        link={`/projects/${project.id}/edit/layout/${userConfig.lang}/?description_yourself`}
                      />
                    </ProjectTabStyles.EditButtonWrapper>
                  )}
                  <AboutOwner />

                  <consent.media.Wall rawMarkup={creatorDescription || ''}>
                    <WysiwygContent
                      loading="lazy"
                      textSize="large"
                      onClickCapture={(e) => {
                        const target = e.target as HTMLElement
                        const anchor = target.closest<HTMLAnchorElement>('a[href]')

                        if (anchor === null) {
                          return
                        }

                        if (anchor.href.includes('?reward=')) {
                          e.preventDefault()

                          const rewardId = new URL(anchor.href).searchParams.get('reward')

                          if (rewardId) {
                            dispatch(modalAction.toggleModalReward(true))
                            dispatch(modalAction.setRewardId(rewardId))
                          }
                        }
                      }}
                    />
                  </consent.media.Wall>
                </>
              </AccordionWrapper>
              {isEditMode && creatorDescription === '' && (
                <ProjectTabStyles.CreateButtonWrapper>
                  <CreateButton
                    createHandler={() => {
                      document.location.href = `/projects/${project.id}/edit/layout/${userConfig.lang}/?description_yourself`
                    }}
                    mode="small"
                    title={<S.heading.XS>{t('About the project owner')}</S.heading.XS>}
                  />
                </ProjectTabStyles.CreateButtonWrapper>
              )}
            </Article>

            <Article isContentSet={!isEditMode}>
              <ProjectFaq />
            </Article>

            {isEditMode && !project.links ? (
              <Article isContentSet={!isEditMode}>
                <ProjectTabStyles.CreateButtonWrapper>
                  <CreateButton
                    createHandler={() => {
                      document.location.href = `/projects/${project.id}/edit/layout/${userConfig.lang}/?form-0-url`
                    }}
                    mode="small"
                    title={<S.heading.XS>{t('Add social media links')}</S.heading.XS>}
                  />
                </ProjectTabStyles.CreateButtonWrapper>
              </Article>
            ) : (
              <>{project.links && <SocialFollow />}</>
            )}
          </Styles.Content>
          <Styles.Sidebar isEditMode={isEditMode}>
            {!projectIsDonationBased ? (
              <>
                <Styles.OnlyDesktopShow isEditMode={isEditMode}>
                  <sidebar.Rewards editMode={isEditMode} onAdviceClick={() => setAdviceModalOpen(true)} />
                </Styles.OnlyDesktopShow>

                <sidebar.Impact editMode={isEditMode} />

                <InvisibleScrollHelper id="sponsors" />

                <sidebar.Sponsors />

                <sidebar.Supporters />
              </>
            ) : (
              <>
                <sidebar.Supporters />

                <Styles.OnlyDesktopShow isEditMode={isEditMode}>
                  <sidebar.Rewards editMode={isEditMode} onAdviceClick={() => setAdviceModalOpen(true)} />
                </Styles.OnlyDesktopShow>

                <sidebar.Impact editMode={isEditMode} />

                <InvisibleScrollHelper id="sponsors" />

                <sidebar.Sponsors />
              </>
            )}
          </Styles.Sidebar>
        </Styles.Wrapper>
        <ExplorerWrapper>
          <Explorer />
        </ExplorerWrapper>
      </Styles.Main>
      <NewToUlule />
      <AdviceModal
        link={`/projects/${project.id}/backoffice/rewards/`}
        open={isAdviceModalOpen}
        setOpen={setAdviceModalOpen}
        title={t('How-to define your rewards')}
        videoHtml='<iframe allow="accelerometer; autoplay; encrypted-media;" allowFullScreen frameBorder="0" src="https://www.youtube.com/embed/EN2SLDQ-oRI?autoplay=1" title="video-placeholder" />'
      />
    </>
  )
}

const Article = styled.div<{ isContentSet?: boolean; open?: boolean }>`
  border-bottom: ${({ open }) => (open !== undefined && !open ? `1px solid ${COLORS.GREY_SHADE_5}` : 'none')};
  margin-bottom: 25px;
  position: relative;

  ${S.heading.L} {
    width: 75%;
    margin-bottom: 21px;
  }

  @media screen and ${BREAKPOINTS.LAPTOP} {
    border-bottom: none;
    margin-bottom: ${({ isContentSet }) => (isContentSet ? '55px' : '17px')};
    padding: 0px;

    ${S.heading.L} {
      width: 100%;
      margin-bottom: 35px;
    }
  }
`

const ArticleHeading = styled.div`
  position: relative;

  ${glyphs.Icon} {
    color: ${COLORS.GREY_SHADE_7};
    position: absolute;
    right: 0px;
    top: 50%;
    transform: translateY(-50%);
  }

  ${S.heading.L} {
    font-size: 26px;
    font-weight: 500;
  }

  @media screen and ${BREAKPOINTS.LAPTOP} {
    ${glyphs.Icon} {
      display: none;
    }

    ${S.heading.L} {
      font-size: 30px;
      font-weight: 900;
    }
  }
`

const ExplorerWrapper = styled.div`
  border-top: 1px solid ${COLORS.GREY_SHADE_5};

  @media screen and ${BREAKPOINTS.LAPTOP} {
    box-sizing: content-box;
    margin: 0 auto;
    max-width: 1200px;
  }
`

const InvisibleScrollHelper = styled.span<{ $offset?: number }>`
  position: relative;
  pointer-events: none;

  ${({ $offset = 0 }) => {
    const value = -80 - $offset
    return css`
      top: ${value}px;
    `
  }}
`
